import React from 'react';
import { FileText, Download, Shield, Building, Eye, Calculator, BookOpen, Award, BadgeCheck, ShieldCheck, Scale, Handshake } from 'lucide-react';

interface Document {
  title: string;
  url: string;
  type: 'pdf' | 'coming-soon';
  isProtected?: boolean;
}

interface DocumentSection {
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  documents: Document[];
  details?: string[];
}

const TransparencyLegal: React.FC = () => {
  const documentSections: DocumentSection[] = [
    {
      title: "Registration & Legal Compliance",
      icon: Building,
      description: "Official registration documents and legal compliance certificates",
      details: [
        "Organization Type: Section 8 Company (Non-Profit)",
        "License No: 167793",
        "Corporate ID: U88900TZ2025NPL034526",
        "Registration Date: 2025",
        "Registrar of Companies: ROC–Tamil Nadu",
        "NPO Darpan ID: TN/2025/0623595"
      ],
      documents: [
        { 
          title: "Certificate of Incorporation", 
          url: "/api/documents/incorporation-certificate", 
          type: "pdf",
          isProtected: true
        },
        { 
          title: "Section 8 License", 
          url: "/api/documents/section8-license", 
          type: "pdf",
          isProtected: true
        },
        { 
          title: "NPO Darpan Registration", 
          url: "/api/documents/npo-darpan", 
          type: "pdf",
          isProtected: true
        },
        { 
          title: "DIPP Certificate (DIPP205239)", 
          url: "/api/documents/dipp-certificate", 
          type: "pdf",
          isProtected: true
        },
        { 
          title: "ISO 9001:2015 Certificate (305025061450Q)", 
          url: "/api/documents/iso-9001-2015-certificate", 
          type: "pdf",
          isProtected: true
        },
        { 
          title: "ISO 14001:2015 Certificate (UK-ARCT-25-14210305)", 
          url: "/api/documents/ISO-14001-2015-certificate", 
          type: "pdf",
          isProtected: true
        },
        ,
        { 
          title: "ISO 26000:2010 Certificate (SR-AD-25091601TN78M2A)", 
          url: "/api/documents/ISO-26000-2010-certificate", 
          type: "pdf",
          isProtected: true
        }
      ]
    },
    {
      title: "Ethics & Integrity",
      icon: Scale,
      description: "Our commitment to transparency, good governance, and zero tolerance for corruption.",
      documents: [
        { 
          title: "Integrity Pledge Certificate", 
          url: "/api/documents/Integrity-Pledge-Certificate", 
          type: "pdf",
          isProtected: true
        }
      ]
    },
    {
      title: "Annual Reports",
      icon: BookOpen,
      description: "Comprehensive annual reports showcasing our activities and impact",
      documents: [
        { title: "Annual Report 2025–26", url: "#", type: "coming-soon" },
        { title: "Annual Report 2026–27", url: "#", type: "coming-soon" }
      ]
    },
    {
      title: "Financial Reports",
      icon: Calculator,
      description: "Audited financial statements and expense breakdowns for transparency",
      documents: [
        { title: "Audited Financial Statement 2025–26", url: "#", type: "coming-soon" },
        { title: "Income & Expense Breakdown 2026–27", url: "#", type: "coming-soon" },
        { title: "Fund Utilization Report", url: "#", type: "coming-soon" }
      ]
    },
    {
      title: "Tax & Compliance",
      icon: Shield,
      description: "Tax exemption certificates and regulatory compliance documents",
      documents: [
        { title: "Income Tax Exemption 12A/80G", url: "/api/documents/Income-Tax-Exemption-12A/80G", type: "pdf", isProtected: true },
        { title: "CSR-1 (CSR00093782)", url: "/api/documents/CSR-1-(CSR00093782)", type: "pdf", isProtected: true },
        { title: "GST Registration Certificate", url: "#", type: "coming-soon" },
        { title: "FCRA Registration", url: "#", type: "coming-soon" }
      ]
    },
    {
      title: "Monitoring & Evaluation",
      icon: Eye,
      description: "Our policies and frameworks for monitoring and evaluating our programs",
      documents: [
        { title: "Monitoring & Evaluation Policy", url: "#", type: "coming-soon" },
        { title: "Impact Assessment Framework", url: "#", type: "coming-soon" },
        { title: "Grievance Redressal Policy", url: "#", type: "coming-soon" }
      ]
    }
  ];

  const handleDocumentClick = async (document: Document) => {
    if (document.type === 'coming-soon') {
      alert('This document will be available soon. We are committed to transparency and will publish all required documents as they become available.');
      return;
    }
    
    if (document.isProtected) {
      try {
        // Create a secure download request
        const response = await fetch(document.url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/pdf',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `${document.title}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          throw new Error('Document not accessible');
        }
      } catch (error) {
        // Fallback to direct URLs for now (since we don't have a backend API yet)
        const directUrls: { [key: string]: string } = {
          '/api/documents/incorporation-certificate': 'https://arrowdropfoundation.com/resources/documents/SPICE + Part B_Approval Letter_AB3223126.pdf',
          '/api/documents/section8-license': 'https://arrowdropfoundation.com/resources/documents/SPICE + Part B_Section 8 License_AB3223126.pdf',
          '/api/documents/npo-darpan': 'https://arrowdropfoundation.com/resources/documents/ARROW_DROP_FOUNDATION.pdf',
          '/api/documents/dipp-certificate': 'https://arrowdropfoundation.com/resources/documents/ARROW DROP FOUNDATION DIPP.pdf',
          '/api/documents/iso-9001-2015-certificate': 'https://arrowdropfoundation.com/resources/documents/iso-9001-2015-certificate.pdf',
          '/api/documents/ISO-14001-2015-certificate': 'https://arrowdropfoundation.com/resources/documents/ISO-14001-2015-certificate.pdf',
          '/api/documents/ISO-26000-2010-certificate': 'https://arrowdropfoundation.com/resources/documents/ARROW DROP FOUNDATION 26000 UKASL.pdf',
          '/api/documents/Integrity-Pledge-Certificate': 'https://arrowdropfoundation.com/resources/documents/VIGILANCE_CERTIFICATE.pdf',
          '/api/documents/Income-Tax-Exemption-12A/80G': 'https://arrowdropfoundation.com/resources/documents/ABCCA7766RE20251_signed.pdf',
          '/api/documents/CSR-1-(CSR00093782)': 'https://arrowdropfoundation.com/resources/documents/CSR-1_Registration letter_AB5479688_19 Jul 2025.pdf.pdf'
        };
        
        const directUrl = directUrls[document.url];
        if (directUrl) {
          window.open(directUrl, '_blank');
        } else {
          alert('Document is currently not accessible. Please try again later or contact us for assistance.');
        }
      }
    } else {
      // For non-protected documents, open directly
      window.open(document.url, '_blank');
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animated-section">
            <div className="mb-8 rounded-full bg-primary/10 p-4 inline-block">
              <Shield className="text-primary w-12 h-12 md:w-16 md:h-16" strokeWidth={2} />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Transparency & Legal
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              We uphold our commitment to openness, compliance, and accountability. 
              All our legal documents and reports are available for public scrutiny.
            </p>
          </div>

          {/* Security Notice */}
          <div className="mb-12 p-6 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 animated-section">
            <div className="flex items-start">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Secure Document Access
                </h3>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Our documents are protected from unauthorized direct access. All downloads are 
                  logged and monitored for security purposes. If you experience any issues accessing 
                  documents, please contact our support team.
                </p>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border text-center animated-section">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-success" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Legally Compliant</h3>
              <p className="text-muted-foreground">
                Registered Section 8 Company with all required certifications and licenses
              </p>
            </div>

            <div className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border text-center animated-section">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Transparent Operations</h3>
              <p className="text-muted-foreground">
                Open access to financial reports, annual reports, and operational documents
              </p>
            </div>

            <div className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border text-center animated-section">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure Access</h3>
              <p className="text-muted-foreground">
                Protected document access with monitoring and security measures in place
              </p>
            </div>
          </div>

          {/* Document Sections */}
          <div className="space-y-12">
            {documentSections.map((section, index) => {
              const IconComponent = section.icon;
              return (
                <div key={index} className="animated-section">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-primary/10 mr-4">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold">{section.title}</h2>
                      <p className="text-muted-foreground">{section.description}</p>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border">
                    {section.details && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">Organization Details</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.details.map((detail, detailIndex) => (
                            <div key={detailIndex} className="flex items-start">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-muted-foreground">{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold mb-4">Available Documents</h3>
                      {section.documents.map((document, docIndex) => (
                        <button
                          key={docIndex}
                          onClick={() => handleDocumentClick(document)}
                          className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-300 text-left ${
                            document.type === 'coming-soon'
                              ? 'border-warning/30 bg-warning/5 hover:bg-warning/10'
                              : 'border-border hover:border-primary/50 hover:bg-primary/5'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`p-2 rounded-lg mr-3 ${
                              document.type === 'coming-soon'
                                ? 'bg-warning/10'
                                : 'bg-primary/10'
                            }`}>
                              <FileText className={`w-5 h-5 ${
                                document.type === 'coming-soon'
                                  ? 'text-warning'
                                  : 'text-primary'
                              }`} />
                            </div>
                            <div>
                              <span className="font-medium">{document.title}</span>
                              {document.type === 'coming-soon' && (
                                <div className="text-sm text-warning">Coming Soon</div>
                              )}
                              {document.isProtected && document.type !== 'coming-soon' && (
                                <div className="text-sm text-blue-600 dark:text-blue-400">
                                  🔒 Secure Download
                                </div>
                              )}
                            </div>
                          </div>
                          <Download className={`w-5 h-5 ${
                            document.type === 'coming-soon'
                              ? 'text-warning'
                              : 'text-muted-foreground'
                          }`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact for More Information */}
          <div className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-8 text-center animated-section">
            <h3 className="text-2xl font-bold mb-4">Need More Information?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              If you need additional documents or have questions about our transparency practices, 
              please don't hesitate to contact us. We believe in complete openness and accountability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:arrowdropfoundation@gmail.com"
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Email Us
              </a>
              <a
                href="tel:+919698862115"
                className="px-6 py-3 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
              >
                Call Us
              </a>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="mt-12 p-6 bg-muted/30 rounded-lg border border-border animated-section">
            <h4 className="font-semibold mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-primary" />
              Legal Notice & Security
            </h4>
            <p className="text-sm text-muted-foreground">
              All documents provided here are authentic and legally verified. Arrow Drop Foundation 
              is committed to maintaining the highest standards of transparency and compliance with 
              all applicable laws and regulations. Document access is monitored and logged for security 
              purposes. For verification of any document, please contact our legal advisor at 
              legal@arrowdropfoundation.com or the relevant regulatory authorities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransparencyLegal;